import { withTranslation } from '@application/i18n/with_translation';
import { ActionCard, Box, Flex, Icon, Text, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import styled from 'styled-components';
import type { RepresentablePatient } from './use-respondent-representation';

const HiddenInput = styled.input`
	display: none;
`;

const InitialsText = styled.span`
	font: ${tokens.text.label.semiBold};
	color: ${tokens.color.content.mutedBrand};
	margin: auto;
`;

const OrDivider = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing.x1};

	&::before,
	&::after {
		content: '';
		flex: 1;
		height: 1px;
		background-color: ${tokens.color.border.muted};
	}
`;

interface RepresentablePatientsPickerProps {
	representablePatients: RepresentablePatient[];
	onSelect: (patient: RepresentablePatient) => void;
	/**
	 * Optional name of a hidden form field to register so the parent form
	 * can track which representable patient was selected.
	 */
	hiddenInputName?: string;
	/**
	 * Whether to render the trailing "or" divider. Disable it when the caller
	 * does not render a fallback NIN input below the picker.
	 */
	showOrDivider?: boolean;
}

/**
 * Renders the list of PU-verified patients the user may represent, plus
 * an "or" divider that callers can use to separate the picker from a
 * fallback NIN input. Shared between the booking and RedRobin triage
 * "who is respondent" screens.
 */
export function RepresentablePatientsPicker({
	representablePatients,
	onSelect,
	hiddenInputName,
	showOrDivider = true,
}: RepresentablePatientsPickerProps) {
	const formContext = useFormContext();
	const hiddenInputProps = hiddenInputName ? formContext?.control.register(hiddenInputName) : undefined;

	if (representablePatients.length === 0) {
		return null;
	}

	return (
		<>
			<Box
				background={tokens.color.bg.surfaceMuted}
				border={`1px solid ${tokens.color.border.muted}`}
				borderRadius={tokens.borderRadius.medium}
				height='unset'
				padding={tokens.spacing.x2}
			>
				<Flex gap={tokens.spacing.x2}>
					<Text variant='labelLargeSemiBold'>{withTranslation('PW.Component.SelectRepresentablePatient.Title')}</Text>
					<Flex direction='row' gap={tokens.spacing.x1} align='center'>
						<Icon type='checkStarFilled' color={tokens.color.focus} />
						<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
							{withTranslation('PW.Component.SelectRepresentablePatient.VerifiedLabel')}
						</Text>
					</Flex>
					{hiddenInputProps ? <HiddenInput type='hidden' {...hiddenInputProps} /> : null}
					<Flex gap={tokens.spacing.x1}>
						{representablePatients.map((person) => (
							<ActionCard.Root
								key={person.Id}
								onClick={() => onSelect(person)}
								icon={<InitialsText>{person.Initials}</InitialsText>}
								trailingIcon={null}
							>
								<ActionCard.Title>{person.CommonName}</ActionCard.Title>
								<ActionCard.Meta>{person.IdentityNumber}</ActionCard.Meta>
							</ActionCard.Root>
						))}
					</Flex>
					<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
						{withTranslation('View.Booking.ScreenWhoIsRespondant.SharingHelper')}
					</Text>
				</Flex>
			</Box>
			{showOrDivider ? (
				<OrDivider>
					<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
						{withTranslation('View.Booking.ScreenWhoIsRespondant.Or')}
					</Text>
				</OrDivider>
			) : null}
		</>
	);
}
